/**
 * Banyomda / Search Intent Router
 *
 * Purpose:
 * - Preserve Shopify native search for open-ended queries.
 * - Route exact, high-confidence category intent to the corresponding
 *   navigation destination instead of returning broad cross-category matches.
 * - Reuse the live primary navigation as the source of truth for URLs.
 *
 * This is intentionally NOT a client-side ranking engine. It never filters
 * or hides individual products and never changes Shopify's result ordering.
 */
(() => {
  const INTENTS = [
    { terms: ['lavabo', 'lavabolar', 'lavabo ürünleri', 'lavabo modelleri'], label: 'Lavabolar' },
    { terms: ['banyo mobilyası', 'banyo mobilyaları', 'banyo dolabı', 'banyo dolapları'], label: 'Banyo Mobilyaları' },
    { terms: ['klozet', 'klozetler'], label: 'Klozetler' },
    { terms: ['asma klozet', 'asma klozetler', 'duvara monte klozet', 'duvara monte klozetler'], label: 'Asma Klozetler' },
    { terms: ['duvara sıfır klozet', 'duvara sıfır klozetler'], label: 'Duvara Sıfır Klozetler' },
    { terms: ['batarya', 'bataryalar'], label: 'Bataryalar' },
    { terms: ['duş', 'duş sistemleri', 'duş seti', 'duş setleri'], label: 'Duş Sistemleri' },
    { terms: ['gömme rezervuar', 'gömme rezervuarlar'], label: 'Gömme Rezervuarlar' },
    { terms: ['banyo aksesuarı', 'banyo aksesuarları', 'aksesuar'], label: 'Banyo Aksesuarları' },
  ];

  const normalize = (value) => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('tr-TR')
    .replace(/ı/g, 'i')
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c')
    .replace(/\s+/g, ' ')
    .trim();

  const normalizedIntents = INTENTS.map((intent) => ({
    ...intent,
    terms: intent.terms.map(normalize),
    normalizedLabel: normalize(intent.label),
  }));

  const findIntent = (query) => {
    const normalizedQuery = normalize(query);
    if (!normalizedQuery || /[:()\-+]/.test(normalizedQuery)) return null;
    return normalizedIntents.find((intent) => intent.terms.includes(normalizedQuery)) || null;
  };

  const getNavigationCandidates = () => {
    const root = document.querySelector('.header__navigation .header__menu, .header__menu');
    if (!root) return [];

    return Array.from(root.querySelectorAll('a[href], summary[data-link]'))
      .map((element) => {
        const rawUrl = element.getAttribute('href') || element.getAttribute('data-link') || '';
        const text = element.textContent?.replace(/\s+/g, ' ').trim() || '';
        if (!rawUrl || rawUrl === '#' || rawUrl.startsWith('javascript:') || !text) return null;
        try {
          return {
            label: text,
            normalizedLabel: normalize(text),
            url: new URL(rawUrl, document.baseURI).toString(),
          };
        } catch {
          return null;
        }
      })
      .filter(Boolean);
  };

  const findRoute = (query) => {
    const intent = findIntent(query);
    if (!intent) return null;

    const candidates = getNavigationCandidates();
    const exact = candidates.find((candidate) => candidate.normalizedLabel === intent.normalizedLabel);
    if (!exact) return null;

    return { label: intent.label, url: exact.url };
  };

  const routeExactIntent = (form) => {
    const input = form.querySelector('input[name="q"]');
    if (!input) return false;

    const query = input.value?.trim() || '';
    const intent = findIntent(query);
    if (!intent) return false;

    // Do not override an explicit product-type scope selected by the user.
    const productType = form.querySelector('#SearchProductTypes')?.value?.trim();
    if (productType) return false;

    const route = findRoute(query);
    if (!route) return false;

    window.location.assign(route.url);
    return true;
  };

  window.BanyomdaSearchIntent = { findRoute, findIntent };

  document.addEventListener('click', (event) => {
    const link = event.target.closest?.('a[href]');
    if (!(link instanceof HTMLAnchorElement)) return;
    if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    let url;
    try {
      url = new URL(link.href, document.baseURI);
    } catch {
      return;
    }

    if (url.origin !== window.location.origin || !url.pathname.endsWith('/search')) return;

    const query = url.searchParams.get('q') || '';
    const route = findRoute(query);
    if (!route || route.url === url.toString()) return;

    event.preventDefault();
    window.location.assign(route.url);
  }, true);

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.getAttribute('role') !== 'search') return;
    if (event.defaultPrevented) return;
    if (event.submitter?.formNoValidate === false) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    if (routeExactIntent(form)) event.preventDefault();
  }, true);
})();
